import { useEffect, useRef } from 'react';
import { createChart, ColorType, CandlestickSeries } from 'lightweight-charts';
import logger from '../utils/logger';

/**
 * Custom hook for managing stock price chart
 * @param {Object} priceHistory - Price history data with data_points array
 * @returns {Object} - { chartContainerRef, chartInstanceRef }
 */
export const useStockChart = (priceHistory) => {
  const chartContainerRef = useRef(null);
  const chartInstanceRef = useRef(null);

  useEffect(() => {
    logger.log('Chart useEffect triggered. priceHistory:', priceHistory ? `${priceHistory.data_points?.length || 0} data points` : 'null', 'chartContainerRef.current:', chartContainerRef.current ? 'exists' : 'null');
    
    if (!priceHistory) {
      logger.log('No priceHistory, skipping chart creation');
      return;
    }
    
    if (!priceHistory.data_points || priceHistory.data_points.length === 0) {
      logger.log('No data points in priceHistory, skipping chart creation');
      return;
    }
    
    // Wait for the DOM element to be available
    const checkRef = () => {
      if (!chartContainerRef.current) {
        logger.log('chartContainerRef not available yet, retrying...');
        setTimeout(checkRef, 50);
        return;
      }
      initializeChart();
    };
    
    let resizeHandler = null;
    
    const initializeChart = () => {
      if (!chartContainerRef.current) {
        logger.error('chartContainerRef still not available after retry');
        return;
      }
      
      logger.log('Creating chart with', priceHistory.data_points.length, 'data points');
      
      // Clean up previous chart instance
      if (chartInstanceRef.current) {
        logger.log('Removing previous chart instance');
        if (resizeHandler) {
          window.removeEventListener('resize', resizeHandler);
        }
        chartInstanceRef.current.remove();
      }

      // Create chart
      const chart = createChart(chartContainerRef.current, {
        layout: {
          background: { type: ColorType.Solid, color: 'white' },
          textColor: '#333',
        },
        width: chartContainerRef.current.clientWidth,
        height: 400,
        grid: {
          vertLines: {
            color: '#e0e0e0',
          },
          horzLines: {
            color: '#e0e0e0',
          },
        },
        timeScale: {
          timeVisible: true,
          secondsVisible: false,
        },
        handleScroll: {
          mouseWheel: false,
          pressedMouseMove: false,
          horzTouchDrag: false,
          vertTouchDrag: false,
        },
        handleScale: {
          mouseWheel: false,
          pinch: false,
          axisPressedMouseMove: false,
          axisDoubleClickReset: false,
        },
      });

      // Add candlestick series (v5 API)
      const candlestickSeries = chart.addSeries(CandlestickSeries, {
        upColor: '#26a69a',
        downColor: '#ef5350',
        borderVisible: false,
        wickUpColor: '#26a69a',
        wickDownColor: '#ef5350',
      });

      // Format data for lightweight-charts
      // First, deduplicate by timestamp (keep the last one if duplicates exist)
      const seenTimes = new Map();
      const chartData = priceHistory.data_points
        .map(point => {
          const time = new Date(point.date).getTime() / 1000;
          return {
            time: time,
            open: point.open,
            high: point.high,
            low: point.low,
            close: point.close,
          };
        })
        .filter(point => point.open !== null && point.high !== null && point.low !== null && point.close !== null)
        .sort((a, b) => a.time - b.time)
        .filter(point => {
          // Deduplicate: if we've seen this timestamp, skip it (keep first occurrence)
          if (seenTimes.has(point.time)) {
            return false;
          }
          seenTimes.set(point.time, true);
          return true;
        });

      logger.log('Chart data prepared:', chartData.length, 'valid data points');
      
      if (chartData.length === 0) {
        logger.error('No valid chart data after filtering');
        return;
      }

      // Set data
      candlestickSeries.setData(chartData);
      logger.log('Chart data set successfully');

      // Fit content to show all data points
      chart.timeScale().fitContent();

      // Handle resize
      resizeHandler = () => {
        if (chartContainerRef.current) {
          chart.applyOptions({ width: chartContainerRef.current.clientWidth });
        }
      };

      window.addEventListener('resize', resizeHandler);
      chartInstanceRef.current = chart;
    };
    
    // Start checking for the ref
    checkRef();
    
    // Cleanup for the useEffect
    return () => {
      if (resizeHandler) {
        window.removeEventListener('resize', resizeHandler);
      }
      if (chartInstanceRef.current) {
        chartInstanceRef.current.remove();
        chartInstanceRef.current = null;
      }
    };
  }, [priceHistory]);

  return { chartContainerRef, chartInstanceRef };
};
